import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import DoneAllIcon from "@mui/icons-material/DoneAll";

export const FinalizarJornadaManual = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalFinalizar, setModalFinalizar] = useState(false);
  const [jornadaSeleccionada, setJornadaSeleccionada] = useState(null);

  const [finManual, setFinManual] = useState("");
  const [motivoManual, setMotivoManual] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resJornadas, resUsuarios] = await Promise.all([
        fetch(`${Global.url}jornadas/proceso`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resJornadas.ok) {
        toast.error("No se pudieron obtener las jornadas en proceso.");
        setJornadas([]);
      } else {
        const data = await resJornadas.json();
        setJornadas(Array.isArray(data) ? data : []);
      }

      if (!resUsuarios.ok) {
        toast.warning("No se pudieron cargar los usuarios (se mostrarán IDs).");
        setUsuarios([]);
      } else {
        const data = await resUsuarios.json();
        setUsuarios(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toast.error("Error de conexión con el servidor.");
      setJornadas([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const getNombreCompleto = (usuario_id) => {
    const u = usuarios.find((x) => String(x.id) === String(usuario_id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${usuario_id ?? "-"}`;
  };

  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";

    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    const d = parseDateSafe(dateValue);
    if (!d) return "";

    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const jornadasVisibles = useMemo(() => {
    if (esAdmin) return jornadas;

    if (esGerente) {
      const idsEquipo = new Set(
        usuarios
          .filter((u) => String(u.gerente_id) === String(auth?.id))
          .map((u) => String(u.id))
      );

      return (jornadas || []).filter((j) => idsEquipo.has(String(j.usuario_id)));
    }

    return (jornadas || []).filter((j) => String(j.usuario_id) === String(auth?.id));
  }, [jornadas, usuarios, auth?.id, esAdmin, esGerente]);

  const abrirFinalizar = (j) => {
    setJornadaSeleccionada(j);
    setFinManual(toDatetimeLocalString(new Date()));
    setMotivoManual("");
    setFormError("");
    setModalFinalizar(true);
  };

  const cerrarFinalizar = () => {
    if (saving) return;
    setModalFinalizar(false);
    setJornadaSeleccionada(null);
    setFinManual("");
    setMotivoManual("");
    setFormError("");
  };

  const finalizarJornada = async () => {
    if (!jornadaSeleccionada) return;

    if (!finManual || !motivoManual.trim()) {
      setFormError("Debes indicar la fecha/hora de fin y un motivo.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}jornadas/finalizar-manualmente`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          id: jornadaSeleccionada.id,
          fin: finManual,
          observaciones_admin: motivoManual.trim(),
        }),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(result?.message || "Error al finalizar la jornada.");
        return;
      }

      toast.success("Jornada finalizada correctamente.");
      cerrarFinalizar();
      await cargarDatos();
    } catch (error) {
      console.error("Error al finalizar:", error);
      toast.error("Error de conexión al finalizar la jornada.");
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(
    () =>
      (jornadasVisibles || []).map((j) => ({
        ...j,
        usuario_nombre: getNombreCompleto(j.usuario_id),
        gerente_nombre: j.gerente_id ? getNombreCompleto(j.gerente_id) : "Sin gerente",
        inicio_txt: fmtDate(j.fecha_inicio),
      })),
    [jornadasVisibles, usuarios]
  );

  const columns = useMemo(() => {
    const base = [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
    ];

    const adminExtra = esAdmin
      ? [{ field: "gerente_nombre", headerName: "Gerente", flex: 1, minWidth: 220 }]
      : [];

    const acciones = [
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
              <Button size="small" startIcon={<DoneAllIcon />} onClick={() => abrirFinalizar(j)}>
                Finalizar
              </Button>
            </Box>
          );
        },
      },
    ];

    return [...base, ...adminExtra, ...acciones];
  }, [esAdmin]);

  const titulo = esAdmin
    ? "Finalizar Jornadas en Proceso"
    : esGerente
    ? "Finalizar Jornadas en Proceso de mi equipo"
    : "Finalizar mis jornadas en proceso";

  const textoSinFilas = esAdmin
    ? "No hay jornadas activas actualmente."
    : esGerente
    ? "No hay jornadas activas actualmente para tu equipo."
    : "No tienes jornadas activas actualmente.";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : textoSinFilas}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        <Dialog open={modalFinalizar} onClose={cerrarFinalizar} fullWidth maxWidth="sm">
          <DialogTitle>Finalizar jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {jornadaSeleccionada
                  ? `${getNombreCompleto(jornadaSeleccionada.usuario_id)} · ${jornadaSeleccionada.centro || ""} · Inicio: ${fmtDate(jornadaSeleccionada.fecha_inicio)}`
                  : ""}
              </Typography>

              <TextField
                label="Fecha/hora fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={finManual}
                onChange={(e) => setFinManual(e.target.value)}
              />

              <TextField
                label="Motivo / observaciones"
                value={motivoManual}
                onChange={(e) => setMotivoManual(e.target.value)}
                placeholder="Motivo del cierre manual"
                multiline
                minRows={3}
              />

              {formError && <Typography color="error">{formError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarFinalizar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={finalizarJornada} disabled={saving}>
              {saving ? "Finalizando..." : "Finalizar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};